import { useState, useEffect, useCallback } from 'react';
import { OKCancel, EditButton, Delete, PlusInput } from '../../lib/iprs-react-library/src/index.js';
import { loadTeamGroups, UNGROUPED_KEY, UNGROUPED_TITLE } from '../Team/teamGroups';
import userLoggedInHasRole from '../Util/userLoggedInHasRole';
import Notice from '../Util/Notice';

const TeamGroupSettings = ({ state, eventDispatch }) => {

    const { api } = state;

    const hasDiaryManagerPerm = userLoggedInHasRole(api, 'Diary Manager');

    const [groups, setGroups] = useState([]);
    const [teams, setTeams] = useState([]);
    const [editingGroup, setEditingGroup] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null); // { kind: 'team' | 'group', id, name } or null
    const [notice, setNotice] = useState(null); // { text, isError }, cleared by the next action

    const loadGroups = useCallback(() => {
        api.transact('TeamGroupList', {})
            .then(r => r.apiResult)
            .then(r => setGroups(r.Groups || []))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to load team groups', isError: true }));
    }, [api]);

    /* Also refreshes the shared team group state (sidebar grouping and the
     * NCAOverlay task-type filter), so changes here show up without a
     * page reload. */
    const loadTeams = useCallback(() => {
        loadTeamGroups(api, eventDispatch)
            .then(({ teams }) => setTeams(teams))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to load teams', isError: true }));
    }, [api, eventDispatch]);

    useEffect(() => { loadGroups(); }, [loadGroups]);
    useEffect(() => { loadTeams(); }, [loadTeams]);

    // Refresh the sidebar's flat team list so a created or deleted team
    // appears or disappears there without needing a full page reload.
    const refreshSidebarTeams = () => {
        api.getTeamList()
            .then(response => response.apiResult)
            .then(teamList => eventDispatch({ team: { teams: teamList } }))
            .catch(err => console.log('Error refreshing sidebar team list', err));
    };

    const onCreateClick = e => {
        e.preventDefault();
        setNotice(null);
        setEditingGroup({ GroupID: 0, Title: e.target.value || '', Description: '', Colour: '#0000FF' });
    };

    const onEditClick = g => {
        setNotice(null);
        setEditingGroup({
            GroupID: g.GroupID,
            Title: g.GroupTitle,
            Description: g.GroupDescription || '',
            Colour: g.GroupColour || '#0000FF'
        });
    };

    const onCancelEdit = () => {
        setNotice(null);
        setEditingGroup(null);
    };

    const onSaveGroup = () => {
        const title = (editingGroup.Title || '').trim();
        if (!title) {
            setNotice({ text: 'Please enter a title for the team group', isError: true });
            return;
        }

        setNotice(null);
        api.transact('TeamGroupUpdate', { ...editingGroup, Title: title })
            .then(() => {
                setEditingGroup(null);
                loadGroups();
                loadTeams();
                setNotice({ text: 'Team group saved', isError: false });
            })
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to save team group', isError: true }));
    };

    const deleteGroup = (groupID, groupTitle) => {
        api.transact('TeamGroupDelete', { GroupID: groupID })
            .then(() => {
                loadGroups();
                loadTeams();
                setNotice({ text: `Team group "${groupTitle}" deleted successfully`, isError: false });
            })
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to delete team group', isError: true }));
    };

    // No success message - the select showing the new group confirms it
    const onAssignTeam = (teamID, groupID) => {
        setNotice(null);
        api.transact('TeamGroupAssignmentUpdate', { TeamID: teamID, GroupID: groupID })
            .then(() => loadTeams())
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to assign team', isError: true }));
    };

    // Soft delete - the team stops appearing in team lists. The API refuses
    // while the team still has future bookings, future rota entries or team
    // members, and its message is shown in the notice.
    const deleteTeam = (teamID, teamName) => {
        api.transact('TeamDelete', { TeamID: teamID })
            .then(() => {
                loadTeams();
                refreshSidebarTeams();
                setNotice({ text: `Team "${teamName}" deleted successfully`, isError: false });
            })
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to delete team', isError: true }));
    };

    const onDeleteClick = pending => {
        setNotice(null);
        setPendingDelete(pending);
    };

    const onConfirmDelete = () => {
        const { kind, id, name } = pendingDelete;
        setPendingDelete(null);
        if ('team' === kind) {
            deleteTeam(id, name);
        } else {
            deleteGroup(id, name);
        }
    };

    const onCancelDelete = () => setPendingDelete(null);

    const onCreateTeam = e => {
        e.preventDefault();
        const teamName = (e.target.value || '').trim();
        if (!teamName) { return; }

        setNotice(null);
        api.transact('SaveTeam', { TeamName: teamName })
            .then(() => {
                loadTeams();
                refreshSidebarTeams();
                setNotice({ text: `${teamName} created`, isError: false });
            })
            .catch(err => setNotice({ text: err.apiText || err.message || 'Failed to create team', isError: true }));
    };

    return (
        <div>
            {/* While the edit overlay is open its errors are shown inside it */}
            {!editingGroup && <Notice notice={notice} onDismiss={() => setNotice(null)} />}

            <section className='settings-section'>
                <h3>Team Groups</h3>
                <div className='settings-list-scroll'>
                    <ul className='settings-list'>
                        {groups.map(g => (
                            <li key={g.GroupID}>
                                <span className='settings-list__label'>{g.GroupTitle}</span>
                                {hasDiaryManagerPerm && (
                                    <div className='settings-list__actions'>
                                        <EditButton onClick={() => onEditClick(g)} value='Edit Team Group'
                                            title='Edit Team Group' enabled={true} />
                                        <Delete onClick={() => onDeleteClick({ kind: 'group', id: g.GroupID, name: g.GroupTitle })}
                                            value={g.GroupID} title='Delete Team Group' enabled={true} />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                {hasDiaryManagerPerm && <PlusInput onClick={onCreateClick} />}
            </section>

            {editingGroup && (
                <>
                    <div className='settings-overlay-backdrop' onClick={onCancelEdit} />
                    <section className='settings-overlay'>
                        <Notice notice={notice} onDismiss={() => setNotice(null)} />
                        <form>
                            <label htmlFor='Title'>Title</label>
                            <input type='text' name='Title' value={editingGroup.Title}
                                onChange={e => setEditingGroup({ ...editingGroup, Title: e.target.value })} size='40' />

                            <label htmlFor='Description'>Description</label>
                            <input type='text' name='Description' value={editingGroup.Description}
                                onChange={e => setEditingGroup({ ...editingGroup, Description: e.target.value })} size='40' />

                            <label htmlFor='Colour'>Colour</label>
                            <input type='color' name='Colour' value={editingGroup.Colour}
                                onChange={e => setEditingGroup({ ...editingGroup, Colour: e.target.value })} />

                            <OKCancel onOK={onSaveGroup} onCancel={onCancelEdit} />
                        </form>
                    </section>
                </>
            )}

            {pendingDelete && (
                <>
                    <div className='settings-overlay-backdrop' onClick={onCancelDelete} />
                    <section className='settings-overlay'>
                        <h3>{'team' === pendingDelete.kind ? 'Delete team' : 'Delete team group'}</h3>
                        <p>Are you sure you want to delete {'team' === pendingDelete.kind ? 'team' : 'team group'} "{pendingDelete.name}"?
                            {'team' === pendingDelete.kind
                                ? ' It will no longer appear in Chronos.'
                                : ' Any teams must be moved out of the group first.'}</p>
                        <OKCancel onOK={onConfirmDelete} onCancel={onCancelDelete} okText='Delete' />
                    </section>
                </>
            )}

            <section className='settings-section'>
                <h3>Assign Teams to Groups</h3>
                <div className='settings-list-scroll'>
                    <ul className='settings-list'>
                        {[...teams]
                            .sort((t1, t2) => t1.TeamName.localeCompare(t2.TeamName))
                            .map(t => (
                                <li key={t.TeamID}>
                                    <span className='settings-list__label'>{t.TeamName}</span>
                                    <div className='settings-list__actions'>
                                        <select value={t.GroupID ?? UNGROUPED_KEY}
                                            disabled={!hasDiaryManagerPerm}
                                            onChange={e => onAssignTeam(t.TeamID, Number.parseInt(e.target.value, 10))} >
                                            <option value={UNGROUPED_KEY}>{UNGROUPED_TITLE}</option>
                                            {groups.map(g => <option key={g.GroupID} value={g.GroupID}>{g.GroupTitle}</option>)}
                                        </select>
                                        {hasDiaryManagerPerm && (
                                            <Delete onClick={() => onDeleteClick({ kind: 'team', id: t.TeamID, name: t.TeamName })}
                                                value={t.TeamID} title='Delete Team' enabled={true} />
                                        )}
                                    </div>
                                </li>
                            ))
                        }
                    </ul>
                </div>
                {hasDiaryManagerPerm && <PlusInput onClick={onCreateTeam} />}
            </section>
        </div>
    );
};

export default TeamGroupSettings;
